import React from "react";
import { Tooltip } from "antd";
import { LockKeyhole } from "lucide-react";
import useLockCopy from "./useLockCopy";
import "./lockTokens.css";
import "./FeatureLockBadge.css";

const sizeClass = {
    small: "commercial-feature-lock-badge--small",
    medium: "commercial-feature-lock-badge--medium",
};

/**
 * FeatureLockBadge — verrou de niveau L1 (subtle).
 *
 * Référence : documentation/docs/ux-audit/locks-premium-audit.md (§3 L1).
 *
 * Badge inline champagne discret signalant une feature premium. Purement
 * visuel : il NE décide PAS du gating. Le parent passe `locked`.
 *
 * Backward-compatible : les props historiques (`locked`, `label`, `tooltip`,
 * `ctaLabel`, `size`) continuent de fonctionner à l'identique. `featureKey`
 * permet désormais de résoudre automatiquement le wording via useLockCopy.
 *
 * @param {boolean} locked          affiche le badge si true, sinon null.
 * @param {string}  [label]         libellé visible (prioritaire sur featureKey).
 * @param {string}  [tooltip]       texte du tooltip (prioritaire sur featureKey).
 * @param {string}  [ctaLabel]      CTA appended dans le tooltip.
 * @param {string}  [featureKey]    clé feature pour résoudre le wording i18n.
 * @param {"small"|"medium"} [size]
 * @param {"subtle"} [variant]      réservé L1 (subtle). Défaut: subtle.
 * @param {boolean} [showCtaInline] affiche le CTA dans le badge (utile mobile).
 * @param {function} [onClick]      handler de clic (alias onLockedClick).
 * @param {function} [onLockedClick]
 */
const FeatureLockBadge = ({
    locked,
    label,
    tooltip,
    ctaLabel,
    featureKey,
    size = "small",
    variant = "subtle",
    showCtaInline = false,
    onClick,
    onLockedClick,
}) => {
    const copy = useLockCopy(featureKey);

    if (!locked) return null;

    const resolvedLabel = label || copy.short;
    const resolvedCtaLabel = ctaLabel || copy.cta;
    const resolvedTooltip = tooltip || copy.tooltip || resolvedLabel;

    const handler = onClick || onLockedClick;
    const interactive = typeof handler === "function";

    const handleClick = (event) => {
        if (!interactive) return;
        event.stopPropagation();
        handler(event);
    };

    const handleKeyDown = (event) => {
        if (!interactive) return;
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleClick(event);
        }
    };

    return (
        <Tooltip
            title={(
                <span>
                    {resolvedTooltip}
                    {resolvedCtaLabel ? ` · ${resolvedCtaLabel}` : ""}
                </span>
            )}
        >
            <span
                className={[
                    "commercial-feature-lock-badge",
                    `commercial-feature-lock-badge--${variant}`,
                    sizeClass[size] || sizeClass.small,
                    interactive ? "commercial-feature-lock-badge--interactive premium-lock-focus" : "",
                ].filter(Boolean).join(" ")}
                aria-label={resolvedLabel}
                role={interactive ? "button" : undefined}
                tabIndex={interactive ? 0 : undefined}
                onClick={interactive ? handleClick : undefined}
                onKeyDown={interactive ? handleKeyDown : undefined}
            >
                <LockKeyhole size={size === "medium" ? 13 : 11} strokeWidth={2} aria-hidden="true" />
                <span className="commercial-feature-lock-badge__label">{resolvedLabel}</span>
                {showCtaInline && resolvedCtaLabel && (
                    <span className="commercial-feature-lock-badge__cta">{resolvedCtaLabel}</span>
                )}
            </span>
        </Tooltip>
    );
};

export default FeatureLockBadge;
